import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@material-ui/core';
import { Formik } from 'formik';
import { toJS } from 'mobx';
import styled from '@emotion/styled';
import { useHistory, Link } from 'react-router-dom';
import moment from 'moment';
import ErrorIcon from '@material-ui/icons/Error';
import CircleIcon from '@material-ui/icons/Lens';
import { useRootStore } from '../../../stores/BridgeProvider';
import { FlexDiv, StandardBox, StyledForm } from '../../styles/styles';
import SimpleSelect from '../../util/SimpleSelect';
import permissionCheck from '../../util/PermCheck';
import ConfirmDialog from '../../dealMapper/common/ConfirmDialog';
import Spinner from '../../util/Spinner';
import { COLORS } from '../../util/Colors';

const StatusBox = styled.div`
  font-size: 0.9rem;
  .issueConnectingMessage {
    font-size: 0.8rem;
    margin-bottom: 10px;
    display: block;
  }
  #statusSpan {
    display: inline-flex;
    align-items: center;
  }
  #errorIcon {
    color: ${COLORS.red};
    font-size: 1rem;
    margin-left: 3px;
  }
  #circleIcon {
    color: ${COLORS.green};
    font-size: 1rem;
    margin-left: 3px;
  }
`;

const ExternalSystemSettings = () => {
  const { orgStore, authStore } = useRootStore();
  const history = useHistory();
  const { settings, isOwner } = orgStore;
  // console.log(toJS(settings));
  const { ExtSysName, ExtSysId } = settings;
  const isIntegrated = ExtSysId > 0;
  const integrationPermissions = permissionCheck('EXTINT_M');
  const [isLoading, setLoading] = useState(true);
  const [intInfo, setIntInfo] = useState(null);

  const sync = async () => {
    try {
      await authStore.request.post(`${process.env.REACT_APP_API}/api/sync`);
    } catch (e) {
      console.error(e);
    }
  };

  const getIntegrationInfo = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/organization-integration-status`,
      );
      setIntInfo(data);
    } catch (e) {
      console.error(e);
      // setLoading(false);
    }
  };

  const getXeroUrl = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/refresh-xero-url`,
      );
      window.location = data;
    } catch (e) {
      console.error(e);
    }
  };

  const disconnectFromXero = async () => {
    try {
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/disconnect-xero-tenant`,
      );
      await orgStore.retrieveConfig();
      history.push('/organization');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const initialActions = async () => {
      await Promise.all([getIntegrationInfo()]);
      setLoading(false);
    };
    initialActions();
  }, []);

  if (isLoading) return <Spinner />;

  if (!isIntegrated) {
    <div>No external system integration found</div>;
  }

  const { LastSync, IsValid } = intInfo;

  const formattedLastSync = moment
    .utc(LastSync)
    .local()
    .format('MM/DD/YYYY hh:mm A');

  const showDisconnectDialog =
    integrationPermissions && isOwner && isIntegrated;

  return (
    <FlexDiv>
      <StandardBox>
        <List>
          <Typography variant="h2" gutterBottom>
            Manage Integration
          </Typography>
          <StatusBox>
            <p>Last Sync: {formattedLastSync}</p>
            <p>
              Status:{' '}
              {IsValid ? (
                <span id="statusSpan">
                  {' '}
                  Active <CircleIcon id="circleIcon" />
                </span>
              ) : (
                <span id="statusSpan">
                  Broken
                  <ErrorIcon id="errorIcon" />
                </span>
              )}
            </p>
            {!IsValid && (
              <span className="issueConnectingMessage">
                There's been an issue connecting with Xero. Please
                reauthenticate below to restore integration
              </span>
            )}
          </StatusBox>
          {!!IsValid && (
            <ListItem
              button
              onClick={sync}
              component={Link}
              to="/sync-in-progress"
            >
              <ListItemText
                primary={`Manual Sync With ${ExtSysName}`}
                secondary="Initiate the sync process with your organization's accounting system"
              />
            </ListItem>
          )}
          <ListItem
            button
            onClick={getXeroUrl}
            // TODO: make this dynamic with various ext systems
          >
            <ListItemText
              primary={`Reauthenticate With ${ExtSysName}`}
              secondary="Redirect to your organization's accounting platform to reauthenticate your Deal Map account"
            />
          </ListItem>
          <ListItem button component={Link} to="/integration-history">
            <ListItemText
              primary="Integration History"
              secondary="See all calls made to external system"
            />
          </ListItem>
          <ListItem button component={Link} to="/integration-errors">
            <ListItemText
              primary="See Integration Errors"
              secondary="View all errors thrown during integration process"
            />
          </ListItem>
          {!!showDisconnectDialog && (
            <ConfirmDialog
              title="Disconnect Deal Map organization from accounting system?"
              content={() => {
                return (
                  <div>
                    You are about to unlink Deal Map from your external
                    accounting system. This will have an irreparable effect on
                    your organization's data. It is recommended you exercise all
                    other options and contact support before proceeding with
                    this course of action.
                  </div>
                );
              }}
              actions={({ handleClose }) => (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    flex: 1,
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      disconnectFromXero();
                      handleClose();
                    }}
                  >
                    Yes
                  </Button>

                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      handleClose();
                    }}
                  >
                    No
                  </Button>
                </div>
              )}
            >
              {({ handleOpen }) => {
                return (
                  <ListItem button onClick={() => handleOpen()}>
                    <ListItemText
                      primary={`Disconnect Org From ${ExtSysName}`}
                      secondary="Deauthenticate Deal Map account from external accounting system"
                      primaryTypographyProps={{ style: { color: 'red' } }}
                    />
                  </ListItem>
                );
              }}
            </ConfirmDialog>
          )}
        </List>
      </StandardBox>
    </FlexDiv>
  );
};

export default ExternalSystemSettings;
